import { z } from 'zod'
import { credentialNameProblem } from '@/lib/gitRouting'
import type { GitRoutingRule } from '@/types/gitRouting'

export type CredentialMode = 'existing' | 'new'

/**
 * The checks the orchestrator makes (`GitRoutingRuleValidation`), made here first so a mistake is shown
 * under its field before a request is sent. The server's answer still wins: it is mapped onto the same
 * fields when it disagrees.
 */
export function routingRuleSchema(rules: GitRoutingRule[], editingId?: string) {
  return z
    .object({
      urlPattern: z.string().trim(),
      provider: z.string().min(1, 'Choose a provider.'),
      credentialMode: z.enum(['existing', 'new']),
      existingCredential: z.string(),
      newCredentialName: z.string().trim(),
      position: z.number().int().min(1),
    })
    .superRefine((values, ctx) => {
      if (!/^https:\/\//i.test(values.urlPattern)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['urlPattern'],
          message: 'The pattern must be a repository URL starting with https://, e.g. https://github.com/owner/*.',
        })
      } else {
        const duplicate = rules.find(
          (r) => r.id !== editingId && r.urlPattern.toLowerCase() === values.urlPattern.toLowerCase(),
        )
        if (duplicate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['urlPattern'],
            message: `A rule for this pattern already exists (rule ${duplicate.position}).`,
          })
        }
      }

      const name = credentialNameOf(values)
      const field = values.credentialMode === 'existing' ? 'existingCredential' : 'newCredentialName'
      if (values.credentialMode === 'existing' && !values.existingCredential) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'Choose a credential.' })
      } else if (values.credentialMode === 'new' && !values.newCredentialName) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'Name the new credential.' })
      } else if (values.provider) {
        const problem = credentialNameProblem(name, values.provider)
        if (problem) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: problem })
        }
      }
    })
}

export type RoutingRuleFormValues = z.infer<ReturnType<typeof routingRuleSchema>>

/** The `{provider}:{name}` the form describes. The new-name prefix is fixed by the provider. */
export function credentialNameOf(values: Pick<RoutingRuleFormValues, 'credentialMode' | 'existingCredential' | 'newCredentialName' | 'provider'>): string {
  return values.credentialMode === 'existing'
    ? values.existingCredential
    : `${values.provider}:${values.newCredentialName.trim()}`
}
